import { useKeycloak } from '@react-keycloak/web';
import { useUnit } from 'effector-react';
import { keycloakLogout } from '@/config/keycloak';
import { $user } from '@/models/user';
import { GroupsEnum } from '@/types/users';
import { Button } from '@/components/ui/Button';

const HELPDESK_EMAIL = 'helpdesk@famil.ru';
const EMAIL_SUBJECT = 'Доступ к неаудируемым системам';
const SYSTEM_NAME = 'SMENA';

const formatAccessGroup = (group: GroupsEnum) => {
  const groupName = group.split('/').filter(Boolean).pop() || group;
  return groupName
    .split('. ')
    .map((part, index) =>
      index === 0 ? SYSTEM_NAME : `${part.charAt(0).toLocaleUpperCase('ru-RU')}${part.slice(1)}`,
    )
    .join('. ');
};

type AccessDeniedProps = {
  requiredGroups: GroupsEnum[];
  mobileOnlyDenied?: boolean;
};

export const AccessDenied = ({ requiredGroups, mobileOnlyDenied }: AccessDeniedProps) => {
  const { keycloak } = useKeycloak();
  const user = useUnit($user);
  const accessGroups = requiredGroups.map(formatAccessGroup);
  const fullName =
    [user?.family_name || user?.secondname, user?.given_name || user?.firstname]
      .filter(Boolean)
      .join(' ') || 'Пользователь';
  const userIdentifier = user?.email || user?.preferred_username;
  const userLabel = userIdentifier ? `${fullName} (${userIdentifier})` : fullName;
  const emailBody = `Прошу предоставить доступ к неаудируемой системе "${SYSTEM_NAME}":\n${accessGroups
    .map((group) => `- ${group}`)
    .join('\n')}`;

  return (
    <main className="flex min-h-[calc(100dvh-64px)] items-center justify-center bg-gray-100 p-6">
      <section className="w-full max-w-xl rounded-xl bg-white p-8 shadow-lg">
        <h1 className="text-center text-2xl font-semibold">Доступ запрещён</h1>
        <p className="mt-4 text-center text-gray-600">
          {userLabel}, у вас недостаточно прав для доступа к этому разделу.
        </p>

        {mobileOnlyDenied ? (
          <p className="mt-5 rounded-lg bg-amber-50 px-4 py-3 text-center text-amber-800">
            Этот раздел доступен только с мобильного устройства.
          </p>
        ) : (
          <>
            <div className="mt-5 rounded-lg bg-gray-50 px-4 py-3">
              <p className="font-medium">Необходимая группа:</p>
              <ul className="mt-2 list-disc pl-5">
                {accessGroups.map((group) => (
                  <li key={group}>{group}</li>
                ))}
              </ul>
            </div>
            <p className="mt-5 text-sm text-gray-600">
              Для получения доступа отправьте запрос на{' '}
              <a
                className="font-medium text-[#631d76] underline"
                href={`mailto:${HELPDESK_EMAIL}?subject=${encodeURIComponent(
                  EMAIL_SUBJECT,
                )}&body=${encodeURIComponent(emailBody)}`}
              >
                {HELPDESK_EMAIL}
              </a>
              .
            </p>
          </>
        )}

        <div className="mt-6 text-center">
          <Button type="button" onClick={() => keycloakLogout(keycloak)}>
            Выйти из системы
          </Button>
        </div>
      </section>
    </main>
  );
};
